import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref } from "vue";
const _hoisted_1 = { class: "criteria-item" };
const _hoisted_2 = { class: "d-flex align-center gap-2 mb-1" };
const _hoisted_3 = {
    key: 2,
    class: "page-chips-container"
};
const _hoisted_4 = {
    key: 0,
    class: "ml-8 mt-2 space-y-1"
};
import { computed, inject } from "vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicyReviewCriteriaItem',
    props: {
        criterion: {},
        currentPageId: { default: null },
        pageManager: { default: null },
        status: { default: null },
        attachedPages: { default: null }
    },
    emits: ["status-change", "page-action", "highlight-evidence"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Inject helper functions from parent
        const getCriterionStatus = inject("getCriterionStatus", (criterion) => criterion.status || "pending");
        const getPageAttachments = inject("getPageAttachments", () => null);
        const hasChildren = computed(() => {
            return props.criterion.children && props.criterion.children.length > 0;
        });
        const currentStatus = computed(() => {
            return props.status || props.criterion.status || "pending";
        });
        const statusIcon = computed(() => {
            if (currentStatus.value === "pass")
                return "mdi-check-circle";
            if (currentStatus.value === "fail")
                return "mdi-close-circle";
            return "mdi-alert-circle";
        });
        const statusColor = computed(() => {
            if (currentStatus.value === "pass")
                return "success";
            if (currentStatus.value === "fail")
                return "error";
            return "warning";
        });
        const statusOptions = [
            { value: "pass", label: "Present" },
            { value: "fail", label: "Missing" },
            { value: "pending", label: "Pending" },
        ];
        const getStatusIcon = (status) => {
            if (status === "pass")
                return "mdi-check-circle";
            if (status === "fail")
                return "mdi-close-circle";
            return "mdi-alert-circle";
        };
        const getStatusColor = (status) => {
            if (status === "pass")
                return "success";
            if (status === "fail")
                return "error";
            return "warning";
        };
        // Pages shown for this criterion, by precedence (no duplicate chips across tiers):
        //   1. manual override (page_attachments entry) — authoritative once it exists, even when empty
        //      (user removed every page); navigate-only, no box.
        //   2. resolved pageEvidence — durable page identity + box; drives the highlight.
        //   3. legacy pageNumbers / pageRef — old orders with no resolved evidence; navigate-only.
        const allPageChips = computed(() => {
            if (props.attachedPages !== null) {
                return props.attachedPages.map((pageId) => ({
                    id: pageId,
                    displayNumber: (props.pageManager?.getPageNumber(pageId) ?? 0) + 1,
                }));
            }
            if (props.criterion.pageEvidence && props.criterion.pageEvidence.length > 0) {
                // Skip evidence whose page isn't in the viewer's page set (getPageNumber === -1) — an
                // unresolvable chip would render "Page 0" and navigate nowhere.
                return props.criterion.pageEvidence
                    .filter((evidence) => (props.pageManager?.getPageNumber(evidence.pageId) ?? -1) !== -1)
                    .map((evidence) => ({
                    id: evidence.pageId,
                    displayNumber: (props.pageManager?.getPageNumber(evidence.pageId) ?? 0) + 1,
                    boundingBox: evidence.boundingBox,
                }));
            }
            const chips = [];
            if (props.criterion.pageNumbers && props.criterion.pageNumbers.length > 0) {
                props.criterion.pageNumbers.forEach((pageNum) => {
                    const pageId = props.pageManager?.pages[pageNum]?.id;
                    if (pageId) {
                        chips.push({ id: pageId, displayNumber: pageNum + 1 });
                    }
                });
            }
            else if (props.criterion.pageRef !== undefined) {
                const pageId = props.pageManager?.pages[props.criterion.pageRef]?.id;
                if (pageId) {
                    chips.push({ id: pageId, displayNumber: props.criterion.pageRef + 1 });
                }
            }
            return chips;
        });
        const onStatusSelect = (newStatus) => {
            emit("status-change", props.criterion.id, newStatus);
        };
        const onAddPage = () => {
            if (props.currentPageId) {
                emit("page-action", props.criterion.id, "add", props.currentPageId);
            }
        };
        const onRemovePage = (pageId) => {
            emit("page-action", props.criterion.id, "remove", pageId);
        };
        // Event handlers for child component events
        const handleStatusChange = (criterionId, newStatus) => {
            emit("status-change", criterionId, newStatus);
        };
        const handlePageAction = (criterionId, action, pageId) => {
            emit("page-action", criterionId, action, pageId);
        };
        const handleHighlightEvidence = (payload) => {
            emit("highlight-evidence", payload);
        };
        // A chip with a box drives navigate+highlight through the emit (the page-preview path handles
        // navigation), so don't also goToPage here — that would double-navigate. Box-less chips navigate only.
        const onChipClick = (pageChip) => {
            if (!props.pageManager)
                return;
            if (pageChip.boundingBox) {
                emit("highlight-evidence", {
                    pageId: pageChip.id,
                    boundingBox: pageChip.boundingBox,
                    fieldKey: props.criterion.text ?? "",
                });
                return;
            }
            const pageIndex = props.pageManager.getPageNumber(pageChip.id);
            if (pageIndex !== -1) {
                props.pageManager.goToPage(pageIndex);
            }
        };
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_PolicyReviewCriteriaItem = _resolveComponent("PolicyReviewCriteriaItem", true);
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    (!hasChildren.value)
                        ? (_openBlock(), _createBlock(_component_v_menu, {
                            key: 0,
                            location: "bottom start"
                        }, {
                            activator: _withCtx(({ props: menuProps }) => [
                                _createVNode(_component_v_btn, _mergeProps(menuProps, {
                                    size: "small",
                                    variant: "text",
                                    color: statusColor.value,
                                    class: "status-selector",
                                    icon: ""
                                }), {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                            icon: statusIcon.value,
                                            size: "small"
                                        }, null, 8, ["icon"])
                                    ]),
                                    _: 2
                                }, 1040, ["color"])
                            ]),
                            default: _withCtx(() => [
                                _createVNode(_component_v_list, null, {
                                    default: _withCtx(() => [
                                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusOptions, (statusOption) => {
                                            return _createVNode(_component_v_list_item, {
                                                key: statusOption.value,
                                                onClick: ($event) => (onStatusSelect(statusOption.value))
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_list_item_title, { class: "d-flex align-center gap-2" }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_icon, {
                                                                icon: getStatusIcon(statusOption.value),
                                                                color: getStatusColor(statusOption.value),
                                                                size: "small"
                                                            }, null, 8, ["icon", "color"]),
                                                            _createTextVNode(" " + _toDisplayString(statusOption.label), 1)
                                                        ]),
                                                        _: 2
                                                    }, 1024)
                                                ]),
                                                _: 2
                                            }, 1032, ["onClick"]);
                                        }), 64))
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        }))
                        : (_openBlock(), _createBlock(_component_v_icon, {
                            key: 1,
                            icon: statusIcon.value,
                            color: statusColor.value,
                            size: "small",
                            class: "status-icon"
                        }, null, 8, ["icon", "color"])),
                    _createElementVNode("span", {
                        class: _normalizeClass(["text-body-2 flex-grow-1", { 'font-weight-medium': hasChildren.value }])
                    }, _toDisplayString(_ctx.criterion.text), 3),
                    (allPageChips.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allPageChips.value, (pageChip) => {
                                return (_openBlock(), _createBlock(_component_v_chip, {
                                    key: pageChip.id,
                                    size: "x-small",
                                    density: "compact",
                                    color: "blue-gray",
                                    class: "cursor-pointer page-chip",
                                    onClick: ($event) => (onChipClick(pageChip)),
                                    "onClick:close": ($event) => (onRemovePage(pageChip.id))
                                }, {
                                    close: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                            icon: "mdi-close",
                                            size: "12"
                                        })
                                    ]),
                                    default: _withCtx(() => [
                                        _createTextVNode(" Page " + _toDisplayString(pageChip.displayNumber), 1)
                                    ]),
                                    _: 2
                                }, 1032, ["onClick", "onClick:close"]));
                            }), 128))
                        ]))
                        : _createCommentVNode("", true),
                    (_ctx.currentPageId)
                        ? (_openBlock(), _createBlock(_component_v_btn, {
                            key: 3,
                            size: "x-small",
                            color: "primary",
                            variant: "text",
                            onClick: onAddPage
                        }, {
                            default: _withCtx(() => _cache[0] || (_cache[0] = [
                                _createTextVNode("Assign Page", -1)
                            ])),
                            _: 1,
                            __: [0]
                        }))
                        : _createCommentVNode("", true)
                ]),
                (hasChildren.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.criterion.children, (child) => {
                            return (_openBlock(), _createBlock(_component_PolicyReviewCriteriaItem, {
                                key: child.id,
                                criterion: child,
                                "current-page-id": _ctx.currentPageId,
                                "page-manager": _ctx.pageManager,
                                status: _unref(getCriterionStatus)(child),
                                "attached-pages": _unref(getPageAttachments)(child.id),
                                onStatusChange: handleStatusChange,
                                onPageAction: handlePageAction,
                                onHighlightEvidence: handleHighlightEvidence
                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                        }), 128))
                    ]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
